import { get, remove, set } from '@intigral/utils/lib/browser-storage/localStorage';
import { getConfiguration } from '../hooks/useConfiguration';
import { STORE_NAME, CACHE_CONFIG_TYPES, PPV_PURCHASE_OPTIONS } from '@intigral/constant';
import { COOKIE_NAMES } from '@intigral/constant';
import { deleteCookie, setCookie } from '@intigral/utils/lib/browser-storage/cookie';
import { DeviceInfo } from '@intigral/domain/lib/core/entities/DeviceInfo';
import type { UserInfo } from '@intigral/domain/lib/core/entities/UserInfo';
import { getOS } from '../helpers/os';
import { JsonType } from '@intigral/types';
import h, { getCountry, getReduxGlobal } from '../helpers/common';
import { appConfig } from '../config/app.config';
import { setUserState } from '../store/reducers/userSlice';
import { applySubscriptionToUser } from './userSubscription';
import { container } from '@intigral/domain';
import { IGeoLocationService } from '@intigral/domain/lib/application/services/GeoLocationService';
import { DI } from '@intigral/constant/config/DIContainer';
import { useConfiguration } from '../hooks/useConfiguration';
import { UserProfile } from './interface';

const isDefaultUser = (user: UserInfo): boolean => {
    return user.operator.toUpperCase() === 'DEFAULT';
};

export const getUserInfo = (key?: keyof UserInfo): UserInfo | any => {
    const user = get(STORE_NAME.USER) as UserInfo;
    return key ? user?.[key] : user;
};

export const getUserTierInfo = (key: string) => {
    const user = get(STORE_NAME.USER) as UserInfo;
    return user?.tierStatus?.[key] ?? null;
};

export const getUserProfileInfo = (key: string) => {
    const profile = get(STORE_NAME.SELECTED_PROFILE) as UserProfile;
    if (!profile) return null;
    return key ? profile[key] ?? null : profile;
};

export type PersistUserOptions = {
    syncRedux?: boolean;
    updateSessionCookie?: boolean;
    updateAppContext?: boolean;
    /** Replace stored user entirely instead of shallow-merge with current. */
    replace?: boolean;
};

/** Default options for a logged-in session write (localStorage + Redux + cookie + context). */
export const PERSIST_SESSION_OPTIONS: Readonly<Required<Pick<PersistUserOptions, keyof PersistUserOptions>>> = {
    syncRedux: true,
    updateSessionCookie: true,
    updateAppContext: true,
    replace: false,
};

const hasAuthFieldsForSessionCookie = (user: Record<string, unknown>): boolean =>
    typeof user.authSign !== 'undefined' &&
    typeof user.username !== 'undefined' &&
    typeof user.authToken !== 'undefined' &&
    typeof user.identityToken !== 'undefined';

type SessionAuthUser = {
    username: string;
    identityToken?: string;
    apiKey?: string;
    authToken: string;
    authSign?: string;
    isGuest?: boolean;
};

/** Writes auth tokens to the USER_SESSION cookie only (does not touch localStorage). */
export const writeUserSessionCookie = (userInfo: UserInfo | SessionAuthUser, expirationInDays: number) => {
    const authData = {
        u: userInfo.username,
        i: userInfo.identityToken,
        a: userInfo.apiKey,
        t: userInfo.authToken,
        s: userInfo.authSign,
        ...(userInfo.isGuest === true ? { isGuest: true } : {}),
    };

    const baseEncodedToken = btoa(JSON.stringify(authData));
    setCookie(COOKIE_NAMES.USER_SESSION, baseEncodedToken, expirationInDays, false, true);
};

/** @deprecated Use writeUserSessionCookie */
export const storeUserInfo = writeUserSessionCookie;

/**
 * Single entry point for persisting the user object to localStorage.
 * Optionally syncs Redux, session cookie, and app context in one pass.
 */
export const persistUser = (patch: Record<string, unknown>, options: PersistUserOptions = {}): UserInfo | any => {
    const { syncRedux = false, updateSessionCookie = false, updateAppContext = false, replace = false } = options;
    const current = (get(STORE_NAME.USER) as UserInfo) || {};
    const merged = replace ? { ...patch } : { ...current, ...patch };

    set(STORE_NAME.USER, merged);

    if (updateSessionCookie && hasAuthFieldsForSessionCookie(merged)) {
        writeUserSessionCookie(merged as unknown as UserInfo, appConfig.userCookieExpirationDays ?? 30);
    }

    if (updateAppContext) {
        h.setContext({ user: merged });
    }

    if (syncRedux) {
        getReduxGlobal()?.dispatch(setUserState(merged));
    }

    return merged;
};

/** Persist with session defaults (Redux, cookie, context). */
export const persistSessionUser = (patch: Record<string, unknown>, options: PersistUserOptions = {}): UserInfo | any =>
    persistUser(patch, { ...PERSIST_SESSION_OPTIONS, ...options });

/** Merge subscription API data into the user, then persist once. */
export const persistUserWithSubscription = (
    baseUser: UserInfo | Record<string, unknown> | null | undefined,
    subscription: Record<string, unknown> | object,
    options: PersistUserOptions = {}
): UserInfo | any => {
    const merged = applySubscriptionToUser(baseUser, subscription);
    return persistSessionUser(merged, { replace: true, ...options });
};

/** LocalStorage-only replace; use persistSessionUser when Redux/cookie/context must stay in sync. */
export const setUser = (user: Record<string, unknown>) => persistUser(user, { replace: true });

export const deleteUser = () => {
    remove(STORE_NAME.USER);
    deleteCookie(COOKIE_NAMES.USER_SESSION);
};

export const deleteUserLoginAttemptCookie = () => {
    deleteCookie(COOKIE_NAMES.USER_LOGIN_ATTEMPT, false);
};

export const isActiveSubscribedUser = (user: UserInfo): boolean => {
    if (isDefaultUser(user)) return true;

    const hasSubscription = user.subscriptionStatus.length > 0;

    if (!hasSubscription) return false;

    for (let i = 0; i < user.subscriptionStatus.length; i++) {
        if (
            user.subscriptionStatus[i]?.statusName === 'ACTIVE' ||
            user.subscriptionStatus[i]?.statusName === 'CANCELLED'
        ) {
            return true;
        }
    }

    return false;
};

export const getUserDeviceInfo = (): DeviceInfo | undefined => {
    return get(STORE_NAME.DEVICE) as DeviceInfo;
};

type OsType = { name: string };
export const isDeviceSupported = (userAgent: string, supportedOS: (OsType & JsonType)[]): boolean => {
    const [osName, osVersion] = getOS(navigator.userAgent);
    const osCondition = supportedOS.find((os) => os.name === osName);
    if (osCondition) {
        return typeof osCondition.version === 'number' && osVersion >= osCondition.version;
    }
    return true;
};

export const getSubStatus = () => {
    const userConfig = get(STORE_NAME.USER) as UserInfo;
    const userStatus = userConfig?.tierStatus?.statusName ?? null;
    const inPromoFlag = userConfig?.tierStatus?.inPromo ?? null;
    const isLoggedIn = !!(userConfig && userConfig.username);

    if (!isLoggedIn) return 'GUEST';
    if (userStatus === 'ACTIVE' && inPromoFlag) return 'FREE_TRIAL';
    if (userStatus === 'ACTIVE' || userStatus === 'CANCELLED') return 'ACTIVE';
    return 'INACTIVE';
};

// export const getCountryPPVEvents = (ppvConfig) => {
//     const countryCode = getCountryCode();
//     // const userPaymentMethod = 'DCB';
//     const userPaymentMethod = getUserPaymentMethod();
//     const ppvEvents =
//         ppvConfig
//             ?.find((country) => country.countryCode === countryCode)
//             ?.paymentMethods?.find((paymentMethod) => paymentMethod.paymentType === userPaymentMethod)?.events ?? [];
//     return ppvEvents;
// };

export const getCountryPPVEvents = (ppvEventConfig, operatorsConfig, countryCode?: string) => {
    // Use provided countryCode or fallback to localStorage for backward compatibility
    let country = countryCode;
    if (!country) {
        // Fallback to localStorage without calling deprecated function
        const cmsConfigs = getConfiguration();
        country = cmsConfigs?.countryCodeThreeLettersMapping?.[getCountry()] || null;
    }
    const operator = getUserInfo('operator')?.toLowerCase?.();

    // Normalize operator to lowercase for consistent lookup

    // Also normalize operatorsConfig keys to lowercase
    const normalizedOperatorsConfig: any = Object.fromEntries(
        Object.entries(operatorsConfig || {}).map(([key, value]) => [key.toLowerCase(), value])
    );

    const userPaymentMethod = normalizedOperatorsConfig?.[operator]?.ppv?.ppvPaymentType || getUserPaymentMethod();
    const ppvEnabled = normalizedOperatorsConfig?.[operator]?.ppv?.enable;

    if (!country || !userPaymentMethod || !ppvEnabled || !Array.isArray(ppvEventConfig)) return [];
    const countryConfig = ppvEventConfig.find((c) => c.countryCode?.toLowerCase() === country?.toLowerCase());
    const events = countryConfig?.paymentMethods?.find(
        (m) => m.paymentType?.toLowerCase() === userPaymentMethod?.toLowerCase()
    )?.events;
    return Array.isArray(events) ? events : [];
};

/**
 * Enhanced function to add isPPV property to channel objects
 * @param {any[]} channels - Array of channel objects to enhance
 * @param {any[]} ppvEventConfig - PPV event configuration array
 * @param {any} operatorsConfig - Operators configuration object
 * @param {string} countryCode - Optional country code for PPV events
 * @returns {any[]} Enhanced channels with isPPV property
 */
export const enhanceChannelsWithPPV = (channels, ppvEventConfig = null, operatorsConfig = null, countryCode = null) => {
    if (!Array.isArray(channels)) return channels;

    // Get PPV events if config is available
    let ppvEvents = [];
    if (ppvEventConfig && operatorsConfig) {
        ppvEvents = getCountryPPVEvents(ppvEventConfig, operatorsConfig, countryCode);
    }

    return channels.map((channel) => {
        let isPPV = false;

        if (Array.isArray(channel.subscriptions)) {
            // Method 1: Check against PPV events config (preferred)
            if (ppvEvents.length > 0) {
                for (const sub of channel.subscriptions) {
                    for (const event of ppvEvents) {
                        if (sub.subscriptionGUID === event.subscriptionGuid) {
                            isPPV = true;
                            break;
                        }
                    }
                    if (isPPV) break;
                }
            }

            // Method 2: Fallback - check subscription naming patterns
            if (!isPPV) {
                isPPV = channel.subscriptions.some((sub) => {
                    const subscriptionGUID = sub.subscriptionGUID?.toLowerCase() || '';
                    const subscriptionTitle = sub.subscriptionTitle?.toLowerCase() || '';

                    // Check if subscription GUID or title contains "ppv"
                    return subscriptionGUID.includes('ppv') || subscriptionTitle.includes('ppv');
                });
            }
        }

        return { ...channel, isPPV: !!isPPV };
    });
};

export const isKidsProfile = (): boolean => {
    const selectedProfile = get(STORE_NAME.SELECTED_PROFILE);
    if (!selectedProfile) {
        return false;
    }
    return selectedProfile.kidsMode === true;
};

export const getSelectedProfileKidsMode = (): boolean => {
    return isKidsProfile();
};
export function getUserPaymentMethod() {
    const userPaymentMethod = getUserTierInfo('paymentMethod');
    // "PF" payment method is considered "CC"
    return userPaymentMethod === PPV_PURCHASE_OPTIONS.PF ? PPV_PURCHASE_OPTIONS.CC : userPaymentMethod;
}

/**
 * @deprecated Use useCountryCode hook instead for better caching and performance
 * This function is kept for backward compatibility during migration
 */
export function getCountryCode() {
    const cmsConfigs = getConfiguration();
    return cmsConfigs?.countryCodeThreeLettersMapping?.[getCountry()] || null;
}

export async function getUserIP() {
    const response = await container.resolve<IGeoLocationService>(DI.SERVICES.IGeoLocationService).getMyLocation();
    return response?.data?.userIP || '';
}

/**
 * @deprecated Use useConfiguration hook instead for better caching and performance
 * This function is kept for backward compatibility during migration
 */
export function getUserConfiguration() {
    const { data: config } = useConfiguration();
    return config;
}

export function getLocalUserData() {
    return get(STORE_NAME.USER) as UserInfo;
}

/**
 * Returns true when the user's operator is listed in configuration `iap_operators`.
 */
export const isIapOperator = (operator?: string | null, iapOperators?: string[] | null): boolean => {
    if (!operator || !Array.isArray(iapOperators) || iapOperators.length === 0) {
        return false;
    }
    const normalizedOperator = operator.toLowerCase();
    return iapOperators.some((op) => op?.toLowerCase() === normalizedOperator);
};
